import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantConfigRepository } from '@repositories/Repository';
import type {
  ConfigKey,
  TenantConfigDTO,
  UpdateTenantConfigDTO,
} from '@repositories/tenantConfigRepository/tenantConfig.types';

export const tenantConfigKeys = {
  all: ['tenantConfigs'] as const,
  lists: () => [...tenantConfigKeys.all, 'list'] as const,
  list: (tenantId: string) => [...tenantConfigKeys.lists(), tenantId] as const,
  details: () => [...tenantConfigKeys.all, 'detail'] as const,
  detail: (tenantId: string, configKey: ConfigKey) => [...tenantConfigKeys.details(), tenantId, configKey] as const,
};

/**
 * Tenant Config 상세 조회
 */
export const useTenantConfigDetail = (tenantId: string, configKey: ConfigKey, options?: UseQueryOptions<string>) =>
  useQuery({
    queryKey: tenantConfigKeys.detail(tenantId, configKey),
    queryFn: async () => {
      const configValue = await tenantConfigRepository.getDetail(tenantId, configKey);
      return configValue;
    },
    ...options,
  });

/**
 * Tenant Config 수정
 */
export const useUpdateTenantConfig = (options?: UseMutationOptions<TenantConfigDTO, Error, UpdateTenantConfigDTO>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTenantConfigDTO) => {
      return tenantConfigRepository.updateTenantConfig(data);
    },
    onSuccess: (data, variables) => {
      // 해당 tenant의 config 목록 및 상세 무효화
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.list(variables.tenantId) });
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.detail(variables.tenantId, variables.configKey) });
    },
    ...options,
  });
};
